import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  signal,
  effect,
  inject,
  DestroyRef,
  input,
} from '@angular/core';

import { IdentityModel } from '@core/models/identity.model';
import { UserModel } from '@core/models/user.model';
import { NgClickOutsideDelayOutsideDirective } from 'ng-click-outside2';

import { AvatarComponent } from '../avatar/avatar.component';
import { PopupUserComponent } from '../select-popup/popup-user/popup-user.component';

interface PopupStyles {
  [key: string]: string;
}

@Component({
  selector: 'ux-avatar-select',
  imports: [AvatarComponent, CommonModule, PopupUserComponent, NgClickOutsideDelayOutsideDirective],
  templateUrl: './avatar-select.component.html',
  styleUrls: ['./avatar-select.component.less'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarSelectComponent {
  private readonly destroyRef = inject(DestroyRef);

  protected readonly projectId = input<string>('');
  @Input() set popupStyle(styles: PopupStyles) {
    this.popupStyle$.set(styles);
  }

  @Output() select = new EventEmitter<string>();
  @Output() remove = new EventEmitter<void>();

  private readonly user$ = signal<UserModel | null>(null);
  private readonly value$ = signal<string>(null);
  private readonly popupStyle$ = signal<PopupStyles>({});
  private readonly isOpenPopup$ = signal<boolean>(false);

  protected readonly popupStyles = computed(() => this.popupStyle$());
  protected readonly isPopupOpen = computed(() => this.isOpenPopup$());
  protected readonly currentUser = computed(() => this.user$());
  protected readonly currentValue = computed(() => this.value$());

  @Input()
  set value(value: string) {
    this.value$.set(value);
  }

  @Input()
  set user(user: UserModel) {
    this.user$.set(user);
  }

  constructor() {
    // Автоматически закрывать попап при уничтожении компонента
    effect(() => {
      if (this.isPopupOpen()) {
        this.destroyRef.onDestroy(() => {
          this.closePopup();
        });
      }
    });
  }

  onTogglePopup(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.isOpenPopup$.update(state => !state);
  }

  closePopup(): void {
    this.isOpenPopup$.set(false);
  }

  selectUser(user: IdentityModel): void {
    this.closePopup();
    this.select.emit(user.id);
  }
}
